import React, { useEffect } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";

import Button from "components/button";
import { defaultSplitInformation } from "mappers";
import { AgentType, ResidualsSplitProfileType } from "types";

import { waterfallCalculation } from "./waterfallCalculation";
import { WaterfallPrimarySplitInformation } from "./waterfallPrimarySplitInformation";
import { WaterfallSecondarySplitInformation } from "./waterfallSecondarySplitInformation";

import styles from "./../../splitProfile.module.scss";

type WaterfallProps = {
	isLoading: boolean;
	setToggleAgentSelect: Function;
	toggleAgentSelect: boolean;
	isEditing: boolean;
	splitProfile: ResidualsSplitProfileType;
	currentAgent?: AgentType;
};

export const Waterfall = ({
	isLoading,
	setToggleAgentSelect,
	toggleAgentSelect,
	isEditing,
	splitProfile,
	currentAgent,
}: WaterfallProps) => {
	const { control, setValue, getValues, watch } = useFormContext();
	const { fields, append, remove } = useFieldArray({
		control,
		name: "secondarySplitInformation",
		keyName: "ide",
	});

	useEffect(() => {
		if (fields.length === 0) {
			append(defaultSplitInformation);
		}
	}, [append, fields.length, remove]);

	const addSplitInformation = () => {
		append(defaultSplitInformation);
	};

	const removeSplitInformation = index => {
		remove(index);
	};
	const fieldsArray = watch("secondarySplitInformation", fields);

	const totalRevenueShare = watch("totalRevenueShare");
	const primarySplitPercent = watch("primarySplitInformation.splitPercent");

	// useEffect for calculation and validation
	//if any secondary agent gets 100% Split then
	//for all next added secondary agents add 0 split by default
	//without ability to edit it unless previous 100% was edited to a lower value
	useEffect(
		() => {
			const formValues = getValues();
			const calculationResults = waterfallCalculation(formValues);
			let lastIndex = fieldsArray.length;

			fieldsArray.forEach((field: any, index: number) => {
				setValue(
					`secondarySplitInformation.${index}.calculation`,
					// index+1 -> due to structure of form`s fieldArray (primarySplitInformation is separate)
					calculationResults[index + 1],
				);

				if (+field.splitPercent === 100) {
					setValue(
						`secondarySplitInformation.${index}.isWaterfallSplitPercentWarning`,
						true,
					);

					for (let i = index + 1; i < fieldsArray.length; i++) {
						setValue(
							`secondarySplitInformation.${i}.splitPercent`,
							0,
						);
						setValue(
							`secondarySplitInformation.${i}.isReadOnlySplitPercent`,
							true,
						);
					}
					lastIndex = index;
				} else {
					setValue(
						`secondarySplitInformation.${index}.isWaterfallSplitPercentWarning`,
						false,
					);
					for (let i = index + 1; i < lastIndex; i++) {
						setValue(
							`secondarySplitInformation.${i}.isReadOnlySplitPercent`,
							false,
						);
					}
				}
			});
			setValue(
				"primarySplitInformation.calculation",
				calculationResults[0],
			);
		},

		/* eslint-disable react-hooks/exhaustive-deps */
		// https://github.com/facebook/react/issues/14476
		[
			JSON.stringify(fieldsArray),
			fields,
			getValues,
			setValue,
			totalRevenueShare,
			primarySplitPercent,
			fieldsArray.length,
		],
	);

	return (
		<>
			<WaterfallPrimarySplitInformation
				name="primarySplitInformation"
				title="Primary Split Information"
				splitInfo={splitProfile?.primarySplitInformation}
				currentAgent={currentAgent}
				isLoading={isLoading}
				isEditing={isEditing}
			/>

			{fields.map((splitInfo: { [key: string]: any }, index) => {
				return (
					<WaterfallSecondarySplitInformation
						key={splitInfo.ide}
						name={`secondarySplitInformation.${index}`}
						title="Secondary Split Information"
						index={index}
						splitInfo={splitInfo}
						isLoading={isLoading}
						setToggleAgentSelect={setToggleAgentSelect}
						toggleAgentSelect={toggleAgentSelect}
						onRemoveSplitInfo={removeSplitInformation}
						isEditing={isEditing}
						isLastItem={fields.length - 1 === index}
					/>
				);
			})}

			<div className={styles.wrappAddButton}>
				<Button
					value="Add Secondary Split Information"
					onClick={addSplitInformation}
					variant="secondary"
				/>
			</div>
		</>
	);
};
